<script lang="ts">
	import { onMount } from 'svelte';
	import { Spinner, Badge } from 'flowbite-svelte';
	import { getServiceDiagnostics } from '../../AppNetwork';
	import type { ServiceDiagnostics } from '../../AppTypes';
	import { statusLabel, statusText, formatDateTime } from '../../statusUtils';

	export let serviceId: number;

	let diag: ServiceDiagnostics | null = null;
	let loading = true;
	let error = '';

	async function load() {
		loading = true;
		error = '';
		try {
			diag = await getServiceDiagnostics(serviceId);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load diagnostics';
		} finally {
			loading = false;
		}
	}
	onMount(load);

	function displayValue(value: number | string | null): string {
		return value === null ? '—' : String(value);
	}
</script>

<div class="p-4 bg-gray-50 dark:bg-gray-800/50">
	{#if loading}
		<div class="flex items-center gap-2 text-sm text-gray-500"><Spinner size="4" /> Loading diagnostics…</div>
	{:else if error}
		<p class="text-sm text-red-500">{error}</p>
	{:else if diag && diag.metrics.length === 0}
		<p class="text-sm text-gray-400">No status metrics configured — this service stays "unknown". Edit it to add metrics &amp; thresholds.</p>
	{:else if diag}
		<div class="space-y-3">
			{#each diag.metrics as m}
				<div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
					<div class="flex flex-wrap items-center justify-between gap-2">
						<div class="flex items-center gap-2">
							<span class="font-mono text-sm text-gray-900 dark:text-white">{m.metricKey}</span>
							<span class="text-xs text-gray-400">via {m.probeName}</span>
							{#if m.evalMode === 'average'}<Badge color="indigo">avg of {m.evalWindow}</Badge>
							{:else if m.evalMode === 'consistent'}<Badge color="indigo">{m.evalWindow} consecutive</Badge>{/if}
							{#if !m.enabled}<Badge color="dark">disabled</Badge>{/if}
						</div>
						<div class="flex items-center gap-3">
							<span class="text-sm">value: <span class="font-mono text-gray-900 dark:text-white">{displayValue(m.value)}</span></span>
							{#if m.enabled}
								<span class="text-sm font-medium {statusText(m.severity)}">{statusLabel(m.severity)}</span>
							{:else}
								<span class="text-sm font-medium text-gray-400" title="Disabled metrics don't affect the service status">not evaluated</span>
							{/if}
						</div>
					</div>

					<!-- Probe diagnostics -->
					<div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
						<span>probe: <span class={statusText(m.probeStatus)}>{statusLabel(m.probeStatus)}</span></span>
						{#if m.probeLastResponseTimeMs !== null}<span>response: {m.probeLastResponseTimeMs} ms</span>{/if}
						<span>last run: {m.probeLastRunAt ? formatDateTime(m.probeLastRunAt) : 'never'}</span>
						{#if m.probeLastError}<span class="text-red-500">error: {m.probeLastError}</span>{/if}
					</div>

					<!-- Thresholds -->
					{#if m.thresholds.length > 0}
						<div class="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 space-y-1">
							{#each m.thresholds as t}
								<div class="flex items-center gap-2 text-xs">
									{#if t.triggered}
										<i class="fa-solid fa-circle-exclamation text-red-500"></i>
									{:else if !t.enabled}
										<i class="fa-regular fa-circle text-gray-300"></i>
									{:else}
										<i class="fa-solid fa-circle-check text-green-500"></i>
									{/if}
									<span class="text-gray-600 dark:text-gray-300">when value <span class="font-mono">{t.comparison} {t.value}</span> → <span class={statusText(t.severity)}>{statusLabel(t.severity)}</span></span>
									{#if !t.enabled}<span class="text-gray-400">(disabled)</span>{:else if t.triggered}<span class="text-red-500 font-medium">triggered</span>{:else}<span class="text-gray-400">ok</span>{/if}
								</div>
							{/each}
						</div>
					{:else}
						<p class="mt-2 text-xs text-gray-400">No thresholds — this metric doesn't affect status.</p>
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</div>
